/* eslint-disable no-restricted-globals */
/* eslint-disable jsx-a11y/role-supports-aria-props */
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { showMessage } from '../../Toastr/ToastService';
import { IHeadProps } from '../TableHead';
import { setLoaderAtom } from '../../../store/recoil/AppState';

function setValue(value: number, type: string, isInPanel = false) {
  let result: any = value;
  if (type === 'time') {
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    const seconds = value - hours * 3600 - minutes * 60;
    if (isInPanel) {
      result =
        (hours ? `${hours}h ` : '') +
        (minutes ? `${minutes}m ` : '') +
        (seconds || (!minutes && !hours) ? `${seconds}s ` : '');
    } else {
      result = `${hours}h ${minutes}m ${seconds}s`;
    }
  } else if (type === 'distance') {
    const kilometers = Math.floor(value);
    const meters = Math.round((value - kilometers) * 1000);
    if (isInPanel) {
      result = (kilometers ? `${kilometers}km ` : '') + (meters || !kilometers ? `${meters}m` : '');
    } else {
      result = `${kilometers}km ${meters}m`;
    }
  }
  return result;
}

function getRegExp(regex: string) {
  const char = regex.charAt(regex.length - 1);
  return char.match(/[a-z]/i)
    ? new RegExp(regex.slice(1, -2), char)
    : new RegExp(regex.slice(1, -1));
}

interface IComponentProps {
  label: boolean;
  action?: (title: string, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  setStateValue: (
    event: React.ChangeEvent<HTMLInputElement>,
    key: string,
    val: string | number | null,
    name: string
  ) => void;
  menuNameOpen: {
    [key: string]: boolean;
  };
}

type Props = IHeadProps & IComponentProps;

function RangeSelect(props: Props): JSX.Element {
  const { formatMessage: f } = useIntl();
  const setLoader = useSetRecoilState<boolean>(setLoaderAtom);

  const actionOpen = (e: any) => {
    props.actionOpen(e, props.columns?.name || '');
  };

  const action = (e: any) => {
    if (props.action) {
      props.action(props.columns?.name || '', e);
    }
  };

  const reset = () => {
    if (props.columns) {
      props.reset(props.columns, props.columns?.name || '', false);
    }
  };

  const loader = (value: boolean) => {
    setLoader(value);
  };

  const handlerActionSave = (e: any) => {
    const onKeyPress = e.currentTarget.getAttribute('data-on-key-press');
    if ((onKeyPress && e.key === 'Enter') || !onKeyPress) {
      const { filterComponent } = props;
      const columnsName = props.columns?.name || '';
      const regex = filterComponent?.regex;
      const subType = filterComponent?.subType;
      const columnsTitle = props.columns?.title || '';
      const { parentState } = props;

      const rangeSelectMin =
        filterComponent && filterComponent.minValue ? filterComponent.minValue : 0;
      const rangeSelectMax =
        filterComponent && filterComponent.maxValue ? filterComponent.maxValue : 0;

      let startMax = 0;
      if (
        !parentState[columnsName] ||
        (parentState[columnsName] &&
          (!parentState[columnsName].startMax || parentState[columnsName].startMax === ''))
      ) {
        startMax = rangeSelectMax;
      } else if (parentState[columnsName].startMax === +parentState[columnsName].startMax) {
        startMax = parentState[columnsName].startMax;
      } else if (regex) {
        const parsedStartMax = String(parentState[columnsName].startMax).match(
          getRegExp(String(regex))
        );
        if (!parsedStartMax) {
          showMessage(
            'toastr.allowedFormat',
            `${setValue(rangeSelectMax, filterComponent?.subType || '')}`,
            'warning'
          );

          return;
        }
        if (parsedStartMax.groups) {
          if (subType === 'time') {
            startMax =
              (parsedStartMax.groups.hours ? Number(parsedStartMax.groups.hours) * 3600 : 0) +
              (parsedStartMax.groups.minutes ? Number(parsedStartMax.groups.minutes) * 60 : 0) +
              (parsedStartMax.groups.seconds ? Number(parsedStartMax.groups.seconds) : 0);
          } else if (subType === 'distance') {
            startMax =
              (parsedStartMax.groups.kilometers ? Number(parsedStartMax.groups.kilometers) : 0) +
              (parsedStartMax.groups.meters ? Number(parsedStartMax.groups.meters) / 1000 : 0);
          }
        }
      } else {
        startMax = parentState[columnsName].startMax;
      }

      let startMin = 0;
      if (
        !parentState[columnsName] ||
        (parentState[columnsName] &&
          (!parentState[columnsName].startMin || parentState[columnsName].startMin === ''))
      ) {
        startMin = rangeSelectMin;
      } else if (parentState[columnsName].startMin === +parentState[columnsName].startMin) {
        startMin = parentState[columnsName].startMin;
      } else if (regex) {
        const parsedStartMin = String(parentState[columnsName].startMin).match(
          getRegExp(String(regex))
        );
        if (!parsedStartMin) {
          showMessage(
            'toastr.allowedFormat',
            `${setValue(rangeSelectMin, filterComponent?.subType || '')}`,
            'warning'
          );

          return;
        }
        if (parsedStartMin.groups) {
          if (subType === 'time') {
            startMin =
              (parsedStartMin.groups.hours ? Number(parsedStartMin.groups.hours) * 3600 : 0) +
              (parsedStartMin.groups.minutes ? Number(parsedStartMin.groups.minutes) * 60 : 0) +
              (parsedStartMin.groups.seconds ? Number(parsedStartMin.groups.seconds) : 0);
          } else if (subType === 'distance') {
            startMin =
              (parsedStartMin.groups.kilometers ? Number(parsedStartMin.groups.kilometers) : 0) +
              (parsedStartMin.groups.meters ? Number(parsedStartMin.groups.meters) / 1000 : 0);
          }
        }
      } else {
        startMin = parentState[columnsName].startMin;
      }

      if (isNaN(startMin) || isNaN(startMax)) {
        showMessage('form.number', 'form.number', 'warning');
      } else if (
        Number(startMin) < Number(rangeSelectMin) ||
        Number(startMax) > Number(rangeSelectMax)
      ) {
        showMessage(
          'toastr.allowedRange',
          `${setValue(rangeSelectMin, filterComponent?.subType || '', true)} - ${setValue(
            rangeSelectMax,
            filterComponent?.subType || '',
            true
          )}`,
          'warning'
        );
      } else if (Number(startMin) > Number(startMax)) {
        showMessage('toastr.rangeSelect', 'toastr.rangeSelect', 'warning');
      } else {
        props.actionSave(
          columnsName,
          { min: Number(startMin), max: Number(startMax) },
          e,
          props.filterComponent?.type || '',
          columnsTitle
        );
      }
    }
  };

  const { columns, filterComponent } = props;
  const name = columns?.name || '';
  const { parentState } = props;

  const rangeSelectMin = filterComponent && filterComponent.minValue ? filterComponent.minValue : 0;
  const rangeSelectMax = filterComponent && filterComponent.maxValue ? filterComponent.maxValue : 0;
  const startMin = parentState[name] ? parentState[name].startMin : rangeSelectMin;
  const startMax = parentState[name] ? parentState[name].startMax : rangeSelectMax;

  return (
    <div
      data-cy={`range-select-popup-${columns?.name}`}
      className={`label ${props.label ? 'label-primary mb-xs position-relative label--filter' : ''} ${
        props.columns !== undefined && props.menuNameOpen[props.columns.name] === props.label
          ? 'open'
          : ''
      }`}
      style={{ padding: !props.label ? '0px' : '9px 8px 8px 8px' }}
    >
      {props.label && filterComponent && (
        <span onClick={actionOpen}>
          <a
            href="#"
            title={f({ id: 'table.filter.button' })}
            data-cy={`range-select-filter-button-${columns?.name}`}
            className="dropdown-toggle text-white text-decoration-none"
            id="dropdownMenu13"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="true"
          >
            {columns?.title}:{' '}
            {filterComponent.selectedValue &&
              `${
                filterComponent.selectedValue.min === null ||
                isNaN(filterComponent.selectedValue.min)
                  ? 0
                  : setValue(filterComponent.selectedValue.min, filterComponent.subType, true)
              } - ${setValue(filterComponent.selectedValue.max, filterComponent.subType, true)}`}
            {filterComponent.sign}
          </a>
          <a
            href="#"
            title={f({ id: 'table.filter.remove' })}
            data-cy="range-select-filter-button-remove"
            className="text-white"
            onClick={action}
            style={{ padding: '8px', margin: '-8px -8px -8px 0' }}
          >
            <i className="cmd-icon cmd-icon-remove-sm" />
          </a>
        </span>
      )}
      <div
        className="dropdown-menu dropdown-menu-custom fs-2 js-dropdown-keep-open"
        aria-labelledby="dropdownMenu13"
      >
        <form>
          <div className="form-group px-2">
            <label
              className="d-block control-label text-left text-black"
              htmlFor="input-time"
              style={{ marginTop: '4px' }}
            >
              {filterComponent && filterComponent.minTitle}
            </label>
            <div className="d-block">
              <div className="open">
                <div
                  className=" form-control-labels"
                  style={{ background: '#fff', border: 'none', color: '#555', padding: '0px' }}
                >
                  <input
                    type="text"
                    data-cy="range-select-ilter-input-from"
                    className="form-control font-weight-normal input-from"
                    id="input-time"
                    autoFocus={true}
                    value={
                      startMin === +startMin && filterComponent
                        ? setValue(startMin, filterComponent.subType)
                        : startMin
                    }
                    onChange={(e) => props.setStateValue(e, 'min', null, name)} // tslint:disable-line
                    onKeyPress={handlerActionSave}
                    data-on-key-press={true}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group px-2 mb-2">
            <label
              className="d-block control-label text-left text-black"
              htmlFor="input-time-to"
              style={{ marginTop: '4px' }}
            >
              {filterComponent && filterComponent.maxTitle}
            </label>
            <div className="d-block">
              <div className="open">
                <div
                  className=" form-control-labels"
                  style={{ background: '#fff', border: 'none', color: '#555', padding: '0px' }}
                >
                  <input
                    type="text"
                    data-cy="range-select-filter-input-to"
                    className="form-control font-weight-normal input-to"
                    id="input-time-to"
                    value={
                      startMax === +startMax
                        ? setValue(startMax, filterComponent?.subType || '')
                        : startMax
                    }
                    onChange={(e) => props.setStateValue(e, 'max', null, name)}
                    onKeyPress={handlerActionSave}
                    data-on-key-press={true}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row m-0 px-1 py-1 pt-2 border-top-on">
            <div className="col-6 px-xs">
              <a
                href="#"
                title={f({ id: 'table.filter.reset' })}
                data-cy="range-select-filter-button-reset"
                id="reset"
                className="e-button d-flex"
                onClick={reset}
              >
                Reset
              </a>
            </div>
            <div className="col-6 px-xs">
              <a
                href="#"
                title={f({ id: 'table.apply' })}
                data-cy="range-select-filter-button-apply"
                className={`e-button e-button--gray d-flex ${
                  props.parentState.max === '' && props.parentState.min === '' ? 'disabled' : ''
                }`}
                onClick={handlerActionSave}
              >
                {f({ id: 'table.apply' })}
              </a>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default React.memo(RangeSelect);
